import axios from "axios";
import { getSessionId } from "./session";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

// ----- Admin token (Commons Admin v1) -----------------------------
// Kept in localStorage under its OWN key, deliberately separate from
// the session id. Admin tokens live for 7 days server-side; we let
// the server be the source of truth on expiry and treat any 401 on
// an admin call as "log the operator out." The token header
// (X-Admin-Token) is only attached when the caller opts in through
// `adminClient` below — ordinary /commons/* requests are never
// admin-authenticated.
const ADMIN_TOKEN_KEY = "commons_admin_token";
export function getAdminToken() {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY) || null; } catch { return null; }
}
export function setAdminToken(t) {
  try { if (t) localStorage.setItem(ADMIN_TOKEN_KEY, t); else localStorage.removeItem(ADMIN_TOKEN_KEY); } catch {}
}

// One axios instance per app, with an interceptor that stamps every request
// with the visitor's anonymous session ID. This is the ONLY thing that
// isolates one demo visitor from another on the backend.
const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers || {};
  cfg.headers["X-Session-Id"] = getSessionId();
  // Intentionally do NOT attach the admin token here. Ordinary
  // /commons/* calls must be treated the same for every visitor
  // regardless of admin login state — otherwise the operator's own
  // browser would silently see-around a Pause. Admin endpoints are
  // reached via `adminClient` below, which is the only place the
  // X-Admin-Token is ever attached.
  return cfg;
});

// Global 503-paused interceptor. A backend `503 + {paused:true}` is
// an EXPECTED application state (Commons Admin has paused the site)
// rather than a caller error, so the paused branch does two things
// that ordinary errors do not:
//
//   1. It redirects the top-level location to `/paused` (unless the
//      visitor is already on `/paused` or on `/admin`, so admins can
//      still log in and reactivate).
//   2. It STALLS the awaited call with a never-resolving Promise
//      instead of `Promise.reject(err)`. The redirect owns the
//      outcome; callers must not continue with fake/empty data, and
//      they must not surface the paused response as an uncaught
//      rejection either. `window.location.replace` is a couple of
//      microtasks away — the stall keeps the React tree in whatever
//      loading state it already had until the navigation lands.
//
// Every OTHER error (generic 503, 500, network failure, timeout)
// falls through to `Promise.reject(err)` unchanged so genuine
// failures still bubble to their existing error surfaces.
client.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 503 && detail && detail.paused === true) {
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        if (path !== "/paused" && path !== "/admin") {
          window.location.replace("/paused");
        }
      }
      return new Promise(() => {});
    }
    return Promise.reject(err);
  },
);

// ----- Admin API client (Commons Admin v1) ------------------------
const adminClient = axios.create({ baseURL: API });
adminClient.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers || {};
  const t = getAdminToken();
  if (t) cfg.headers["X-Admin-Token"] = t;
  return cfg;
});

export async function adminStatus() {
  const r = await adminClient.get(`/admin/status`);
  return r.data;
}
export async function adminLogin(password) {
  const r = await adminClient.post(`/admin/login`, { password });
  return r.data;  // { token, expiresAt }
}
export async function adminLogout() {
  try { await adminClient.post(`/admin/logout`); } catch {}
  setAdminToken(null);
}
export async function adminSetPaused(paused) {
  const r = await adminClient.post(`/admin/pause`, { paused });
  return r.data;
}
export async function adminMoveProject(projectId, to) {
  const r = await adminClient.post(`/admin/library/move`, { projectId, to });
  return r.data;
}
export async function adminVisits() {
  const r = await adminClient.get(`/admin/visits`);
  return r.data;  // { total: int, daily: [{date, count}] }
}
export async function adminInterests() {
  const r = await adminClient.get(`/admin/interests`);
  return r.data;  // { total, items: [{id, email, name, interests, message, createdAt}] }
}
export async function adminInterestsCsv() {
  const r = await adminClient.get(`/admin/interests.csv`, { responseType: "blob" });
  return r.data;
}
export async function adminRefreshDelivery() {
  const r = await adminClient.post(`/admin/interests/refresh-delivery`);
  return r.data;  // { updated }
}

// Public Get Involved form. Own instance: not session-scoped, never paused.
const publicClient = axios.create({ baseURL: API });
export async function submitInterest(payload) {
  const r = await publicClient.post(`/interest`, payload);
  return r.data;
}
// Lightweight read of the global Overflow list (public, pause-exempt).
export async function getOverflowIds() {
  const r = await publicClient.get(`/admin/status`);
  return r.data.overflowProjectIds || [];
}

// Visitor analytics (Feb 2026). One opaque per-browser id in
// localStorage; server dedups per-(day,browserId) and per-browserId.
// The hit endpoint is on /visits/*, which is NOT behind the pause
// gate, so a visitor who lands during a Pause is still counted.
// Fires from a lightweight beacon in App.js on any non-/admin route.
const BROWSER_ID_KEY = "commons.browserId";

export function getBrowserId() {
  try {
    let v = window.localStorage.getItem(BROWSER_ID_KEY);
    if (!v || !/^[A-Za-z0-9_-]{8,128}$/.test(v)) {
      // 128 bits of randomness → 22 base64 chars. Opaque, no PII.
      const bytes = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      v = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
      window.localStorage.setItem(BROWSER_ID_KEY, v);
    }
    return v;
  } catch {
    return null;
  }
}

// A dedicated axios instance for the visit beacon: does NOT go
// through the ordinary 503-redirect interceptor, so a paused hit
// stays silent instead of triggering navigation.
const visitsClient = axios.create({ baseURL: API });
export async function recordVisit() {
  const browserId = getBrowserId();
  if (!browserId) return;
  try {
    await visitsClient.post(`/visits/hit`, { browserId });
  } catch {
    // Analytics is best-effort. Never surface, never redirect.
  }
}

export async function getCommons() {
  const r = await client.get(`/commons`);
  return r.data;
}

export async function resetCommons() {
  const r = await client.post(`/commons/reset`);
  return r.data;
}

export async function onboard(name, phone) {
  const r = await client.post(`/commons/onboard`, { name, phone });
  return r.data;
}

export async function updateMe(personId, patch) {
  const r = await client.post(`/commons/me`, { personId, patch });
  return r.data;
}

export async function makePledge(personId, projectId, requirementId, quantity = 1) {
  const r = await client.post(`/commons/pledge`, { personId, projectId, requirementId, quantity });
  return r.data;
}

export async function setQuantity(personId, projectId, requirementId, quantity) {
  const r = await client.post(`/commons/pledge-quantity`, { personId, projectId, requirementId, quantity });
  return r.data;
}

export async function withdraw(personId, projectId, requirementId) {
  const body = { personId, projectId };
  if (requirementId) body.requirementId = requirementId;
  const r = await client.post(`/commons/withdraw`, body);
  return r.data;
}

export async function declineInvite(personId, projectId) {
  const r = await client.post(`/commons/decline`, { personId, projectId });
  return r.data;
}

export async function createProject(project) {
  const r = await client.post(`/commons/project`, { project });
  return r.data;
}

export async function updateProject(id, patch) {
  const r = await client.patch(`/commons/project/${id}`, { patch });
  return r.data;
}

export async function launchProject(id) {
  const r = await client.post(`/commons/project/${id}/launch`);
  return r.data;
}

export async function completeProject(id, story) {
  const r = await client.post(`/commons/project/${id}/story`, { story });
  return r.data;
}

export async function addCoSteward(projectId, personId) {
  const r = await client.post(`/commons/project/${projectId}/costeward`, { personId });
  return r.data;
}

export async function inviteToProject(projectId, inviterId, inviteeName, inviteePhone, note) {
  const r = await client.post(`/commons/project/${projectId}/invite`, {
    inviterId, inviteeName, inviteePhone, note: note || null,
  });
  return r.data;
}
